import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { money } from "../../lib/api";
import { SAMPLE_INVOICES } from "../../lib/sampleData";
import { SamplePreviewBanner, SampleChip, SampleInvoiceDialog } from "../../components/SamplePreview";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Zap, Shield, Handshake, Sparkles, Undo2, Pencil, CreditCard, Clock, Receipt, Search, Filter, Download, MoreHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";
import AdjustInvoiceModal from "../../components/AdjustInvoiceModal";
import SellInvoiceModal from "../../components/SellInvoiceModal";
import InvoiceExpiryChip from "../../components/InvoiceExpiryChip";
import IssueCreditMemoDialog from "../../components/IssueCreditMemoDialog";
import InvoiceAmountWithEvents from "../../components/InvoiceAmountWithEvents";
import PaymentsTabBar from "../../components/PaymentsTabBar";

export default function SellerInvoices() {
  const loc = useLocation();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [memoTarget, setMemoTarget] = useState(null);
  const [sellTarget, setSellTarget] = useState(null);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendTerms, setExtendTerms] = useState("net30");
  const [extendReason, setExtendReason] = useState("");
  const [extendQuote, setExtendQuote] = useState(null);
  const [extendBusy, setExtendBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Pagination — same shape as WalletActivityPanel so the UX rhyme
  // is consistent across the whole hub.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
  // Light-touch cross-link: when arriving from an Activity row's
  // invoice chip (`?focus=<id>`), scroll that row into view and flash
  // the highlight for ~3s so the user instantly spots it.
  const focusId = new URLSearchParams(loc.search).get("focus") || null;
  useEffect(() => {
    if (!focusId || items.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-invoice-row="${focusId}"]`);
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [focusId, items]);

  // Filtered list — search by code or buyer, plus status filter.
  const filtered = useMemo(() => {
    return items.filter(i => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (i.code || "").toLowerCase().includes(q)
          || (i.buyer_name || "").toLowerCase().includes(q);
    });
  }, [items, search, statusFilter]);

  // Reset to page 1 whenever filters or search change so the user
  // doesn't end up on an empty page 7 after typing a query.
  useEffect(() => { setPage(1); }, [search, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * pageSize;
  const pageEnd    = pageStart + pageSize;
  const paged      = filtered.slice(pageStart, pageEnd);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("q", search.trim());
      const qs = params.toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/export.csv${qs ? "?" + qs : ""}`;
      const token = localStorage.getItem("token");
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `verqo-invoices-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Could not export CSV");
    }
  };

  const load = async () => {
    const [iRes, oRes] = await Promise.all([api.get("/invoices"), api.get("/orders")]);
    setItems(iRes.data);
    setOrders(oRes.data);
  };
  useEffect(() => { load(); }, []);

  // Note: legacy "Factor" button removed Jun 2026 in favor of the
  // marketplace-bid "Get paid today" flow (`SellInvoiceModal`).
  // Backend route `/invoices/{iid}/factor` remains for backward
  // compatibility but no UI surface drives it now.

  const insure = async (id) => {
    const { data } = await api.post("/insurance", { invoice_id: id });
    toast.success(`Insured at ${money(data.premium)} premium (${(data.coverage_pct*100)}% coverage)`);
    load();
  };

  const openExtendCredit = async (invoice) => {
    setExtendTarget(invoice);
    setExtendTerms("net30");
    setExtendReason("");
    setExtendQuote(null);
    try {
      const { data } = await api.get("/loans/fee-quote", { params: { amount: invoice.amount, terms: "net30" } });
      setExtendQuote(data);
    } catch (e) {
      // 423 credit_line_disabled is handled by the dialog itself
      const err = e?.response?.data;
      if (err?.detail?.code === "credit_line_disabled" || err?.detail === "credit_line_disabled") {
        toast.error("Credit line is not enabled. Contact support to activate.");
        setExtendTarget(null);
      } else {
        toast.error(err?.detail || "Failed to load fee quote");
      }
    }
  };

  const refreshQuote = async (terms) => {
    setExtendTerms(terms);
    try {
      const { data } = await api.get("/loans/fee-quote", { params: { amount: extendTarget.amount, terms } });
      setExtendQuote(data);
    } catch {}
  };

  const submitExtendCredit = async () => {
    if (!extendTarget) return;
    setExtendBusy(true);
    try {
      const { data } = await api.post(`/seller/invoices/${extendTarget.id}/extend-credit`,
        { terms: extendTerms, reason: extendReason || undefined });
      toast.success(`Net-${extendTerms.replace("net","")} extended to buyer · ${data.loan_number}`);
      setExtendTarget(null);
      load();
    } catch (e) {
      const err = e?.response?.data?.detail || e?.response?.data;
      if (err?.code === "credit_line_disabled" || err === "credit_line_disabled") {
        toast.error("Credit line is not enabled. Contact support.");
      } else if (err?.reason === "loan_already_exists") {
        toast.error(`Loan already exists (${err.loan_number || "—"})`);
      } else {
        toast.error(err?.message || err || "Extend-credit failed");
      }
    } finally { setExtendBusy(false); }
  };

  return (
    <>
      <PageHeader title="Invoices" subtitle="Get paid today on any eligible invoice or insure for protection. Pre-shipment qty discrepancies → use Adjust. Post-delivery problems → use Claims (Orders → Claims)."/>

      {/* Sticky tab strip — shared with Payouts hub. Keeps the visual
          rhythm consistent across /seller/wallet and /seller/invoices. */}
      <PaymentsTabBar role="seller"/>

      <div className="p-8 space-y-4">
        {/* Filter / search / export bar */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[180px]">
            <Search className="w-4 h-4 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice # or buyer…"
              className="pl-9 h-10"
              data-testid="si-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-44" data-testid="si-status-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5"/>
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paying">Paying</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="credit_terms">On terms</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
              <SelectItem value="fully_refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[11.5px] text-foreground/55 ml-1">
            {filtered.length} of {items.length}
          </div>
          <Button variant="outline" className="rounded-full h-10 ml-auto"
                  onClick={exportCsv}
                  data-testid="si-export-csv">
            <Download className="w-3.5 h-3.5 mr-1.5"/>Export CSV
          </Button>
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-5 py-3 font-medium">Invoice</th>
                <th className="px-5 py-3 font-medium">Buyer</th>
                <th className="px-5 py-3 font-medium">Terms</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && items.length > 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-foreground/55" data-testid="si-empty">
                  No invoices match your filter.
                </td></tr>
              )}
              {paged.map(i => {
                const refunded = Number(i.refunded_total || 0);
                const cmTotal = Number(i.credit_memo_total || 0);
                const linkedOrder = orders.find(o => o.id === i.order_id);
                const orderShipped = ["transit", "delivered", "cancelled"].includes(linkedOrder?.status);
                const adjustable = ["draft", "sent", "paying", "paid"].includes(i.status) && !orderShipped && !i.fully_refunded;
                // Seller can extend credit on unpaid invoices that haven't already been turned into a loan
                const canExtendCredit = !i.financed && !i.fully_refunded
                  && !i.loan_id
                  && ["draft", "sent", "paying", "overdue"].includes(i.status);
                // Free-form credit memo (post-sale refund) — available on any
                // paid/shipped/delivered/credit_terms/overdue invoice, regardless
                // of order shipment status. Distinct from Adjust which forces
                // line-by-line qty edits.
                const canCreditMemo = ["paid", "paying", "shipped", "delivered",
                                       "credit_terms", "overdue"].includes(i.status)
                                       && !i.fully_refunded;
                // Seller can SELL invoice (factoring/advance) when buyer has accepted Net-X terms
                // and the invoice hasn't already been sold or paid
                const canSell = !i.sold_to_lender_org_id && !i.fully_refunded
                  && ["sent", "paying", "credit_terms"].includes(i.status)
                  && ["net14", "net30", "net60", "net90", "net120", "custom"].includes((i.terms || "").toLowerCase());
                return (
                  <tr key={i.id} data-testid={`inv-${i.code}`}
                      data-invoice-row={i.id}
                      className={focusId === i.id ? "bg-accent/15 animate-pulse" : ""}
                  >
                    <td className="px-5 py-4 mono">
                      <span className="inline-flex items-center gap-1.5">
                        {i.code}
                        {i.kind === "pre_credit_deposit" && (
                          <span data-testid={`pcd-chip-${i.code}`}
                                className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded text-[9.5px] mono uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 normal-case">
                            Pre-credit
                          </span>
                        )}
                        {i.kind === "saas_subscription" && (
                          <span data-testid={`saas-chip-${i.code}`}
                                className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded text-[9.5px] mono uppercase tracking-wider bg-violet-100 text-violet-800 border border-violet-200 normal-case">
                            verqo SaaS
                          </span>
                        )}
                        {i.is_test && (
                          <span data-testid={`test-badge-${i.code}`} className="inline-flex items-center px-1.5 h-[18px] rounded text-[9.5px] mono uppercase tracking-wider bg-orange-100 text-orange-800 border border-orange-200">TEST</span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        {i.buyer_name}
                        {i.buyer_number && (
                          <span
                            data-testid={`invoice-buyer-number-${i.code}`}
                            title="Customer #"
                            className="text-[10px] mono text-foreground/55"
                          >#{i.buyer_number}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 uppercase text-xs">
                      {i.terms}
                      {/* Phase SC-4 — clock-anchor visibility for the seller. */}
                      {(i.terms_start_event || "ship_date") === "ship_date"
                        && /^(net14|net30|net60|net90|net120|custom)$/i.test(i.terms || "")
                        && ["sent","outstanding","overdue","credit_terms"].includes(i.status)
                        && (i.carrier_shipped === false ? (
                          <div data-testid={`si-clock-pending-${i.code}`} className="mt-1 inline-flex items-center gap-1 px-1.5 h-[18px] rounded text-[9.5px] normal-case tracking-normal bg-amber-50 text-amber-800 border border-amber-200">
                            <Clock className="w-2.5 h-2.5"/>Clock starts on shipment
                          </div>
                        ) : i.carrier_shipped === true ? (
                          <div data-testid={`si-clock-running-${i.code}`} className="mt-1 inline-flex items-center gap-1 px-1.5 h-[18px] rounded text-[9.5px] normal-case tracking-normal bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <Clock className="w-2.5 h-2.5"/>Clock running
                          </div>
                        ) : null)}
                    </td>
                    <td className="px-5 py-4">
                      <InvoiceAmountWithEvents
                        invoiceId={i.id}
                        amount={i.amount}
                        subText={
                          <>
                            {refunded > 0 && (
                              <div className="text-[10px] text-foreground/45 mono mt-0.5" data-testid={`refunded-${i.code}`}>
                                −{money(refunded)} refunded
                              </div>
                            )}
                            {cmTotal > 0 && (
                              <div className="text-[10px] text-emerald-700 mono mt-0.5" data-testid={`cm-total-${i.code}`}>
                                −{money(cmTotal)} credit memo
                              </div>
                            )}
                          </>
                        }
                      />
                    </td>
                    <td className="px-5 py-4">
                      <span className={`chip ${i.status === "paid" ? "bg-accent/30 border border-accent/40" : i.status === "overdue" ? "bg-destructive/10 text-destructive" : i.status === "credit_terms" ? "bg-sky-50 text-sky-700 border border-sky-200" : i.status === "voided" ? "bg-foreground/10 text-foreground/55 border border-foreground/15" : "bg-secondary"}`}>{i.status}</span>
                      {/* Phase IV — invoice validity countdown so the seller
                       *  can see at a glance which invoices are at risk of
                       *  auto-voiding. Only renders for pre-acceptance invoices
                       *  with an expires_at stamp. */}
                      <span className="ml-2 inline-block align-middle">
                        <InvoiceExpiryChip invoice={i} compact/>
                      </span>
                      {i.voided_reason === "validity_expired" && (
                        <span className="ml-2 chip bg-rose-50 text-rose-700 border border-rose-200" data-testid={`expired-validity-${i.code}`}
                              title="Validity window elapsed before buyer paid or authorized.">
                          Auto-voided
                        </span>
                      )}
                      {i.insured && <span className="ml-2 chip bg-foreground text-background"><Shield className="w-3 h-3"/> Insured</span>}
                      {i.fully_refunded && (
                        <span className="ml-2 chip bg-rose-50 text-rose-700 border border-rose-200" data-testid={`fully-refunded-${i.code}`}>
                          <Undo2 className="w-3 h-3"/>Fully refunded
                        </span>
                      )}
                      {i.payment_intent === "seller_terms" && (
                        <span className="ml-2 chip bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`free-terms-${i.code}`}>
                          <Handshake className="w-3 h-3"/>Your free {(i.terms || "").toUpperCase()}
                        </span>
                      )}
                      {i.extension_terms && (
                        <span className="ml-2 chip bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <Sparkles className="w-3 h-3"/>Extended {i.extension_terms.toUpperCase()}
                        </span>
                      )}
                      {i.loan_number && (
                        <span className="ml-2 chip bg-violet-50 text-violet-700 border border-violet-200" data-testid={`loan-${i.code}`}>
                          <CreditCard className="w-3 h-3"/>{i.loan_number}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {/* New action layout (Jun 2026): 3 primary inline CTAs
                          + overflow kebab. See PRD changelog. */}
                      <div className="flex items-center gap-1.5 justify-end">
                        {/* PRIMARY · Get paid today — verqo Capital marketplace bid */}
                        {canSell && (
                          <Button size="sm"
                            onClick={() => setSellTarget(i)}
                            data-testid={`sell-invoice-${i.code}`}
                            className="rounded-full bg-lime-400 text-slate-900 hover:bg-lime-500">
                            <Sparkles className="w-3.5 h-3.5 mr-1"/>Get paid today
                          </Button>
                        )}
                        {i.sold_to_lender_org_id && (
                          <span className="chip bg-lime-50 text-lime-800 border border-lime-200" data-testid={`sold-badge-${i.code}`}>
                            <Sparkles className="w-3 h-3"/>Sold to lender
                          </span>
                        )}

                        {/* PRIMARY · Insure — Allianz Trade credit protection */}
                        {!i.insured && (
                          <Button size="sm" variant="outline" onClick={()=>insure(i.id)} data-testid={`insure-${i.code}`} className="rounded-full">
                            <Shield className="w-3.5 h-3.5 mr-1"/>Insure
                          </Button>
                        )}

                        {/* PRIMARY · Credit memo — post-sale free-form refund */}
                        {canCreditMemo && (
                          <Button size="sm" variant="outline" onClick={() => setMemoTarget(i)}
                                  data-testid={`credit-memo-${i.code}`}
                                  className="rounded-full text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                            <Receipt className="w-3.5 h-3.5 mr-1"/>Credit memo
                          </Button>
                        )}

                        {/* OVERFLOW · Adjust (pre-ship qty) + Extend credit (one-time terms swap) */}
                        {(adjustable || canExtendCredit) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline"
                                data-testid={`row-overflow-${i.code}`}
                                className="rounded-full w-9 h-9 p-0">
                                <MoreHorizontal className="w-4 h-4"/>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel className="text-[10.5px] mono uppercase tracking-[0.15em] text-foreground/55">
                                More actions
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator/>
                              {adjustable && (
                                <DropdownMenuItem
                                  onClick={() => setAdjustTarget(i)}
                                  data-testid={`adjust-${i.code}`}>
                                  <Pencil className="w-3.5 h-3.5 mr-2"/>Adjust line qty
                                </DropdownMenuItem>
                              )}
                              {canExtendCredit && (
                                <DropdownMenuItem
                                  onClick={() => openExtendCredit(i)}
                                  data-testid={`extend-credit-${i.code}`}>
                                  <CreditCard className="w-3.5 h-3.5 mr-2"/>Extend credit
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-0" data-testid="seller-invoices-empty">
                    {/* Sample preview — renders when seller has zero real
                     *  invoices. Shows 3 fake invoices in different states
                     *  (paid/outstanding/with shipping-passthrough). Click
                     *  any row → opens detail dialog with hardcoded data. */}
                    <SampleInvoicesPreview />
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {filtered.length > 0 && (
            <div className="px-5 md:px-6 py-3 border-t border-border flex items-center justify-between flex-wrap gap-3"
                 data-testid="si-pager">
              <div className="text-[11px] text-foreground/55 mono">
                Showing <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageEnd, filtered.length)}</strong> of <strong>{filtered.length}</strong>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-foreground/55 mono">Rows</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  data-testid="si-page-size"
                  className="h-8 rounded-md border border-border bg-secondary/40 px-2 text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <Button variant="ghost" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  data-testid="si-prev"
                  className="h-8 rounded-full px-3">
                  <ChevronLeft className="w-3.5 h-3.5 mr-1"/>Prev
                </Button>
                <div className="text-[11px] mono text-foreground/70 px-1">
                  Page <strong>{safePage}</strong> / {totalPages}
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  data-testid="si-next"
                  className="h-8 rounded-full px-3">
                  Next<ChevronRight className="w-3.5 h-3.5 ml-1"/>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AdjustInvoiceModal
        open={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        invoice={adjustTarget}
        onSuccess={() => load()}
      />

      <IssueCreditMemoDialog
        open={!!memoTarget}
        onClose={() => setMemoTarget(null)}
        invoice={memoTarget}
        onSuccess={() => load()}
      />

      <SellInvoiceModal
        open={!!sellTarget}
        invoice={sellTarget}
        onClose={() => setSellTarget(null)}
        onSuccess={() => load()}
      />

      <Dialog open={!!extendTarget} onOpenChange={() => !extendBusy && setExtendTarget(null)}>
        <DialogContent data-testid="extend-credit-dialog">
          <DialogHeader>
            <DialogTitle>Extend Net-X credit · {extendTarget?.code}</DialogTitle>
            <DialogDescription>
              You'll own the receivable. The buyer's wallet will be auto-debited
              on the due date · Allianz covers you at day-60.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Buyer</span><span className="font-medium">{extendTarget?.buyer_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Invoice amount</span><span className="font-medium tabular-nums">{money(extendTarget?.amount)}</span></div>
            </div>
            <div>
              <Label>Net-X term</Label>
              <Select value={extendTerms} onValueChange={refreshQuote}>
                <SelectTrigger data-testid="extend-credit-terms"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="net14">Net-14 (1% fee)</SelectItem>
                  <SelectItem value="net30">Net-30 (2% fee)</SelectItem>
                  <SelectItem value="net60">Net-60 (4% fee)</SelectItem>
                  <SelectItem value="net90">Net-90 (6% fee)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {extendQuote && (
              <div className="rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-sm space-y-1" data-testid="extend-credit-quote">
                <div className="flex justify-between"><span>Buyer fee</span><span className="font-medium tabular-nums">{money(extendQuote.fee_breakdown?.buyer_fee_usd)}</span></div>
                <div className="flex justify-between text-slate-500"><span>Your share (90%)</span><span className="tabular-nums">{money((extendQuote.fee_breakdown?.buyer_fee_usd || 0) * 0.9)}</span></div>
                <div className="flex justify-between text-slate-500"><span>Verqo (10%)</span><span className="tabular-nums">{money((extendQuote.fee_breakdown?.buyer_fee_usd || 0) * 0.1)}</span></div>
                <div className="flex justify-between font-medium pt-1 border-t border-violet-200"><span>Buyer pays at due-date</span><span className="tabular-nums">{money(extendQuote.total_owed_by_buyer)}</span></div>
              </div>
            )}
            <div>
              <Label>Note (optional)</Label>
              <Input data-testid="extend-credit-reason" value={extendReason}
                onChange={e => setExtendReason(e.target.value)}
                placeholder="Granting Net-30 as discussed last week"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendTarget(null)} disabled={extendBusy}>Cancel</Button>
            <Button data-testid="extend-credit-submit" onClick={submitExtendCredit} disabled={extendBusy}>
              {extendBusy ? "Submitting…" : `Extend ${extendTerms.toUpperCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SampleInvoicesPreview — empty-state preview wired into SellerInvoices.
// Shows 3 fake invoices (paid / outstanding / paid-with-shipping-passthrough).
// ─────────────────────────────────────────────────────────────────────────────
function SampleInvoicesPreview() {
  const [sampleInv, setSampleInv] = useState(null);
  return (
    <div data-testid="sample-invoices-preview" className="text-left">
      <SamplePreviewBanner noun="Invoices"/>
      {SAMPLE_INVOICES.map(inv => {
        const tone =
          inv.status === "paid"     ? "bg-emerald-500/15 text-emerald-700" :
          inv.status === "overdue"  ? "bg-rose-500/15 text-rose-700" :
                                       "bg-amber-500/15 text-amber-800";
        return (
          <button
            type="button"
            key={inv.id}
            onClick={() => setSampleInv(inv)}
            data-testid={`sample-invoice-row-${inv.id}`}
            className="w-full grid grid-cols-[1fr_1.4fr_120px_110px_140px_120px] gap-3 px-5 py-3 items-center border-b border-border opacity-[0.78] hover:opacity-100 hover:bg-secondary/40 transition-all"
          >
            <div className="font-mono text-[12.5px] text-slate-700 flex items-center gap-1.5">
              {inv.code}
              <SampleChip />
            </div>
            <div className="text-[13px] text-slate-700 truncate">{inv.buyer_name}</div>
            <div>
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${tone}`}>
                {inv.status}
              </span>
            </div>
            <div className="text-[12px] text-slate-600 uppercase">{inv.terms.replace(/_/g, "-")}</div>
            <div className="text-[13px] font-medium text-slate-700 tabular-nums">${inv.amount.toLocaleString()}</div>
            <div className="text-right text-[11px] text-foreground/40">Open →</div>
          </button>
        );
      })}
      <SampleInvoiceDialog invoice={sampleInv} onClose={() => setSampleInv(null)} />
    </div>
  );
}

